import type { KeyboardEvent } from 'react';
import { Link2, Mail, MailOpen, Pin, PinOff, Star, Tag } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { cn } from '@/lib/utils';
import { avatarGradientClass } from '@/lib/avatar-gradient';
import { formatPhone } from '@/lib/format-phone';
import { formatRelativeTime } from '@/lib/format-time';
import { ACCOUNT_COLOR_PALETTE } from '@/features/accounts/account-colors';
import { formatAccountLabel } from '@/features/accounts/accounts.api';
import { useAccounts } from '@/features/accounts/useAccounts';
import { useAccountColors } from '@/features/accounts/useAccountColors';
import {
  CUCI_TAG,
  SPECIAL_TAG,
  type Conversation,
} from '@/features/conversations/conversations.api';
import { useConversationFlags } from '@/features/conversations/useConversationFlags';
import { useUIStore } from '@/store/ui';

interface MergedConversationRowProps {
  /** The run's grouping key — `groupKeyOf` of every member (raw, un-encoded);
   *  becomes the `ThreadSelection.key` on click. */
  groupKey: string;
  /** The run's conversations in SERVER ORDER (block head first — the newest
   *  unpinned member for an unpinned block, but the newest PIN for a
   *  fully-pinned block, which is PIN order, NOT activity order; see
   *  `DisplayRow`). The head supplies contact identity only — preview /
   *  timestamp come from the most recently ACTIVE member (see the `display`
   *  member in the component body). The collapse guarantees length >= 2 — a
   *  single-member "run" renders as an ordinary `ConversationRow` instead. */
  members: Conversation[];
  /** The FULL set of conversation ids sharing this row's group key —
   *  regardless of pinnedness — passed by `ConversationList`. A pin-split
   *  customer (partially pinned) renders as TWO blocks sharing the key, and
   *  the thread opened from either must carry EVERY sibling's id: the WS
   *  mirror, viewing unread-suppression, viewed auto-read POST and
   *  ThreadPanel's mark-read fan-out all key off `thread.memberIds`, so a
   *  set built from one block alone would let the other block's unread
   *  badges climb while the operator is viewing that very customer. When
   *  omitted, `handleSelect` falls back to this block's own member ids. */
  threadMemberIds?: string[];
  /** Virtualizer measurement hook — see `ConversationRow`'s prop of the same
   *  name; the OUTER wrapper owns measureRef / style / data-index. */
  measureRef?: (node: HTMLElement | null) => void;
  style?: React.CSSProperties;
  dataIndex?: number;
}

/**
 * ONE row standing in for a whole same-phone run in the aggregate ("所有账号")
 * inbox (feat/merged-phone-thread) — the customer talked to 2+ of our shop
 * numbers, and instead of N stacked per-account rows the agent sees a single
 * merged card:
 *
 *   - primary line: the customer's phone (house phone-first style), plus a
 *     link glyph + member count marking it as a merged row;
 *   - secondary line: the contact display name (from the block head) and,
 *     right-aligned, one account color DOT per member (existing palette,
 *     block order) — the shop numbers involved, at a glance;
 *   - preview / timestamp: the most recently ACTIVE member's (max
 *     `last_message_at` over the block — NOT necessarily the block head:
 *     a fully-pinned block is ordered by pin time, so its head can be a
 *     quiet member; see the `display` member below), with the same pin
 *     glyph as `ConversationRow` beside the timestamp when the block is
 *     pinned;
 *   - unread badge: the SUM over members (falls back to the numberless
 *     attention dot when the sum is 0 but any member is manually marked).
 *
 * Clicking activates the merged THREAD (`setSelectedThread`) rather than a
 * single conversation — `ChatPanel` renders `ThreadPanel` for it, and the
 * store's mutual exclusivity clears any single-conversation selection.
 *
 * Right-click offers 置顶, 标记未读, 要cuci and Special, each fanned out to
 * EVERY member (N PATCHes via the existing per-conversation endpoint — flags
 * stay per-account in v1). 置顶 is meaningful here since
 * feat/merged-pin-and-special-filter: the server's grouped ordering gained a
 * GROUP-LEVEL PIN ANCHOR that keeps a FULLY-pinned same-key block contiguous
 * inside the pinned band, so "pin the merged row" = pin every member and the
 * block floats to the pinned band as one row (`collapseGroupRuns` merges
 * uniformly-pinned runs). A partially-pinned customer still splits — the
 * pinned member escapes as its own pinned row/block while the unpinned
 * members stay a normal block — which is why the fan-out drives every member
 * to ONE shared target state.
 *
 * No claim/release/mark-read action cluster: claims are per-conversation
 * (v1 keeps them unchanged), and opening the thread already fans out the
 * mark-read POSTs (see `ThreadPanel`).
 */
export function MergedConversationRow({
  groupKey,
  members,
  threadMemberIds,
  measureRef,
  style,
  dataIndex,
}: MergedConversationRowProps): React.JSX.Element {
  const selectedThread = useUIStore((s) => s.selectedThread);
  const setSelectedThread = useUIStore((s) => s.setSelectedThread);
  const isSelected = selectedThread?.key === groupKey;

  // Accounts + colors for the member dots. Both hooks read caches kept warm
  // by `AccountSidebar` / `ConversationList` — no fetch of their own.
  const accountsQuery = useAccounts();
  const accountColors = useAccountColors();
  const accountLabelById = new Map(
    (accountsQuery.data ?? []).map((a) => [a.id, formatAccountLabel(a)]),
  );

  const flags = useConversationFlags();

  // The block HEAD (members[0], server order) supplies the row's CONTACT
  // identity (name / phone / avatar) — every member shares one customer, so
  // any member would do; the head keeps it deterministic. It must NOT supply
  // preview / timestamp: a FULLY-PINNED block is ordered by pinned_at DESC
  // (newest PIN first), not by activity, so the head can be a quiet member
  // while an older-pinned sibling keeps receiving messages — and nothing
  // ever reorders a pinned block (the projector patches pinned rows in
  // place; refetches re-import the same pin order), so a head-based preview
  // would go permanently stale next to a climbing unread sum.
  const head = members[0];

  // The DISPLAY member — max `last_message_at` over the block — supplies
  // preview + timestamp instead. `last_message_at` is an ISO-8601 UTC
  // string, so plain lexicographic `>` compares chronologically (a null
  // coalesces to '', sorting before every real timestamp). For an unpinned
  // block this IS the head (the server orders those members by recency), so
  // only fully-pinned blocks actually diverge.
  const display = members.reduce(
    (best, m) =>
      (m.last_message_at ?? '') > (best.last_message_at ?? '') ? m : best,
    members[0],
  );

  // Attention state: unread badge = SUM over members; the manual flag shows
  // the numberless dot when the sum is zero (same precedence as a single row).
  const unreadSum = members.reduce((sum, m) => sum + m.unread_count, 0);
  const anyMarkedUnread = members.some((m) => m.marked_unread);

  // Aggregate flag states for the context-menu labels. "All" (not "any")
  // decides the label so the offered ACTION is always meaningful for at
  // least one member: if any member is still unmarked, 标记未读 marks the
  // stragglers; only when every member is marked does the label flip to the
  // clearing action. Same rule for the 要cuci / Special tags and the pin.
  const allMarkedUnread = members.every((m) => m.marked_unread);
  const anyHasCuci = members.some((m) => m.tags.includes(CUCI_TAG));
  const allHaveCuci = members.every((m) => m.tags.includes(CUCI_TAG));
  const anyHasSpecial = members.some((m) => m.tags.includes(SPECIAL_TAG));
  const allHaveSpecial = members.every((m) => m.tags.includes(SPECIAL_TAG));

  // Pinnedness. By the collapse rule a block's members are UNIFORMLY pinned
  // or unpinned (`collapseGroupRuns` never fuses across a pinnedness change),
  // so the head member is authoritative for the row visual — but the fan-out
  // below still computes `allPinned` defensively so a transiently mixed
  // members array (mid-mutation cache states) converges on one target.
  const isPinned = head.pinned_at !== null;
  const allPinned = members.every((m) => m.pinned_at !== null);

  const contactName =
    head.contact.display_name?.trim() ||
    head.contact.phone_e164 ||
    head.contact.peer_id;
  const initials = computeInitials(contactName);

  const hasPhone = !!head.contact.phone_e164;
  const primaryText = hasPhone
    ? formatPhone(head.contact.phone_e164)
    : contactName;
  const showNameRow = hasPhone && !!head.contact.display_name?.trim();

  function handleSelect(): void {
    // Pin-split blocks must not fragment the thread's member set: a
    // partially-pinned customer renders as TWO blocks (pinned + unpinned)
    // sharing this key, and the thread opened from EITHER must carry every
    // sibling's id — the WS mirror, viewing unread-suppression, viewed
    // auto-read and ThreadPanel's mark-read fan-out all read
    // `thread.memberIds` (history is complete either way; the server
    // fetches by key). `ConversationList` passes the full-key set as
    // `threadMemberIds`; the own-members fallback keeps tests and any other
    // caller working unchanged.
    setSelectedThread({
      key: groupKey,
      memberIds: threadMemberIds ?? members.map((m) => m.id),
    });
  }

  function handleRowKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  }

  // --- Context-menu fan-out (GLOBAL flags, applied to EVERY member) --------
  // One PATCH per member via the existing per-conversation endpoint; each
  // mutation carries only the field it toggles. Members already at the
  // target value are skipped (the PATCH is idempotent, but N-1 no-op
  // round-trips are still N-1 wasted requests).

  function handlePinFanOut(): void {
    // "Pin the merged row" = drive EVERY member to the same pinnedness. The
    // server's grouped pin anchor then keeps the fully-pinned block merged
    // in the pinned band (sorted by its newest pin). Skipping members
    // already at the target matters beyond the wasted request here: the
    // server only re-stamps `pinned_at` when the value actually flips, so
    // the skip also preserves an existing member's pin time.
    const target = !allPinned;
    for (const member of members) {
      if ((member.pinned_at !== null) === target) continue;
      flags.mutate({ conversationId: member.id, patch: { pinned: target } });
    }
  }

  function handleMarkedUnreadFanOut(): void {
    const target = !allMarkedUnread;
    for (const member of members) {
      if (member.marked_unread === target) continue;
      flags.mutate({
        conversationId: member.id,
        patch: { marked_unread: target },
      });
    }
  }

  function handleCuciFanOut(): void {
    const target = !allHaveCuci;
    for (const member of members) {
      const has = member.tags.includes(CUCI_TAG);
      if (has === target) continue;
      // tags REPLACES the whole array server-side — send each member's own
      // full desired set so unrelated tags survive (see ConversationRow).
      const nextTags = target
        ? [...member.tags, CUCI_TAG]
        : member.tags.filter((tag) => tag !== CUCI_TAG);
      flags.mutate({ conversationId: member.id, patch: { tags: nextTags } });
    }
  }

  function handleSpecialFanOut(): void {
    // Same all-vs-any convention and whole-array PATCH discipline as the
    // cuci fan-out — only the Special membership changes per member.
    const target = !allHaveSpecial;
    for (const member of members) {
      const has = member.tags.includes(SPECIAL_TAG);
      if (has === target) continue;
      const nextTags = target
        ? [...member.tags, SPECIAL_TAG]
        : member.tags.filter((tag) => tag !== SPECIAL_TAG);
      flags.mutate({ conversationId: member.id, patch: { tags: nextTags } });
    }
  }

  return (
    // OUTER wrapper: owns the virtualizer contract (measureRef / style /
    // data-index) and the PADDING-based gutter — same measurement contract
    // as ConversationRow (margins are excluded from getBoundingClientRect,
    // so a margin gap would make cards overlap).
    <div
      ref={measureRef}
      data-index={dataIndex}
      style={style}
      className="px-4 py-1.5"
      data-testid="merged-conversation-row"
    >
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            role="button"
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={handleRowKeyDown}
            aria-pressed={isSelected}
            className={cn(
              'neu-pressable group flex w-full cursor-pointer items-center gap-3 rounded-2xl border-l-[3px] border-transparent px-3 py-3 text-left text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
              !isSelected &&
                'shadow-neu-raised hover:shadow-neu-raised-hover active:shadow-neu-inset-press',
              isSelected && 'bg-[var(--bsel)] shadow-neu-selected',
            )}
          >
            <Avatar className="size-10 shrink-0 shadow-neu-flat">
              <AvatarFallback
                className={cn(
                  'text-[13px] font-extrabold text-white',
                  avatarGradientClass(head.contact.id),
                )}
              >
                {initials}
              </AvatarFallback>
            </Avatar>

            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'min-w-0 truncate text-[13.5px] font-extrabold',
                    hasPhone && 'tabular-nums',
                    isSelected && 'text-[hsl(10,80%,50%)]',
                  )}
                >
                  {primaryText}
                </span>
                {/* Merged marker: link glyph + member count. */}
                <span
                  data-testid="merged-count"
                  aria-label={`合并 ${members.length} 个会话`}
                  className="flex shrink-0 items-center gap-0.5 text-[10.5px] font-bold text-muted-foreground"
                >
                  <Link2 aria-hidden="true" className="size-3 shrink-0" />
                  {members.length}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                {showNameRow ? (
                  <p className="min-w-0 flex-1 truncate text-xs font-semibold text-muted-foreground">
                    {head.contact.display_name?.trim()}
                  </p>
                ) : (
                  // Empty spacer keeps `justify-between` two-children so the
                  // dot cluster stays right-aligned (see ConversationRow).
                  <span aria-hidden="true" />
                )}
                <div className="flex shrink-0 items-center gap-1.5">
                  {anyHasCuci && (
                    <Badge
                      variant="outline"
                      data-testid="cuci-pill"
                      className="shrink-0 rounded-lg border-transparent px-2 py-0.5 text-[9.5px] font-bold leading-none bg-[linear-gradient(145deg,#f5cf4e,#e5b93a)] text-[#7a5c00] shadow-[2px_2px_4px_var(--neu-dark-mid)]"
                    >
                      {CUCI_TAG}
                    </Badge>
                  )}
                  {anyHasSpecial && (
                    <Badge
                      variant="outline"
                      data-testid="special-pill"
                      className="shrink-0 rounded-lg border-transparent px-2 py-0.5 text-[9.5px] font-bold leading-none bg-[linear-gradient(145deg,#c9b8f5,#ab93e8)] text-[#3b1d7a] shadow-[2px_2px_4px_var(--neu-dark-mid)]"
                    >
                      {SPECIAL_TAG}
                    </Badge>
                  )}
                  {/* One account color dot per member — the shop numbers this
                      customer has talked to, in block (server) order. Same
                      dot treatment as ThreadPanel's header. */}
                  <span
                    className="flex shrink-0 items-center gap-1"
                    role="list"
                    aria-label="会话成员账号"
                  >
                    {members.map((member) => {
                      const color =
                        accountColors.get(member.account_id) ??
                        ACCOUNT_COLOR_PALETTE[0];
                      const label =
                        accountLabelById.get(member.account_id) ??
                        member.account_id;
                      return (
                        <span
                          key={member.id}
                          role="listitem"
                          data-testid="merged-account-dot"
                          title={label}
                          aria-label={label}
                          className={cn(
                            'size-2.5 shrink-0 rounded-full',
                            color.swatch,
                          )}
                        />
                      );
                    })}
                  </span>
                </div>
              </div>
              <p className="truncate text-xs font-semibold text-muted-foreground">
                {display.last_message_preview ?? <em>No messages yet</em>}
              </p>
            </div>

            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <span className="flex items-center gap-1 text-[10.5px] font-bold text-neu-faint">
                {/* Pin indicator — same treatment as ConversationRow: sits
                    just left of the timestamp. A merged block is uniformly
                    pinned, so the head member speaks for the whole row. */}
                {isPinned && (
                  <Pin
                    data-testid="pinned-icon"
                    aria-label="pinned"
                    className="size-3 shrink-0 text-primary"
                  />
                )}
                {formatRelativeTime(display.last_message_at)}
              </span>
              <div className="flex items-center gap-1.5">
                {unreadSum > 0 ? (
                  <Badge
                    variant="unread"
                    className="h-5 min-w-[20px] justify-center rounded-full px-1.5 text-[10px] leading-none"
                    aria-label={`${unreadSum} unread`}
                  >
                    {unreadSum}
                  </Badge>
                ) : anyMarkedUnread ? (
                  <Badge
                    variant="unread"
                    data-testid="marked-unread-dot"
                    aria-label="marked unread"
                    className="size-2.5 rounded-full p-0"
                  />
                ) : null}
              </div>
            </div>
          </div>
        </ContextMenuTrigger>
        {/* Fan-out flag actions, one PATCH per member needing change. 置顶 IS
            offered here since feat/merged-pin-and-special-filter: the
            server's grouped pin anchor keeps a fully-pinned same-key block
            MERGED inside the pinned band, so pinning the merged row (= every
            member) floats the whole block there as one row instead of
            exploding it into singles. Labels follow the all-vs-any
            convention documented on the aggregate flags above. */}
        <ContextMenuContent>
          <ContextMenuItem onSelect={handlePinFanOut}>
            {allPinned ? <PinOff /> : <Pin />}
            {allPinned ? '取消置顶' : '置顶'}
          </ContextMenuItem>
          <ContextMenuItem onSelect={handleMarkedUnreadFanOut}>
            {allMarkedUnread ? <MailOpen /> : <Mail />}
            {allMarkedUnread ? '清除未读标记' : '标记未读'}
          </ContextMenuItem>
          <ContextMenuItem onSelect={handleCuciFanOut}>
            <Tag />
            {allHaveCuci ? '取消要cuci' : '标记要cuci'}
          </ContextMenuItem>
          <ContextMenuItem onSelect={handleSpecialFanOut}>
            <Star />
            {allHaveSpecial ? '取消Special' : '标记Special'}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </div>
  );
}

// --- Helpers ---------------------------------------------------------------

/** Same initials logic as ConversationRow / ThreadPanel — keep in sync. */
function computeInitials(source: string): string {
  const trimmed = source.trim();
  if (!trimmed) return '??';
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) {
    return (tokens[0][0] + tokens[1][0]).toUpperCase();
  }
  return tokens[0].slice(0, 2).toUpperCase();
}
